import { useParams } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ProducerLayout } from '@/components/producer/ProducerLayout';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useProducerById, useUpdateProducer } from '@/hooks/useMyProducers';
import { toast } from '@/hooks/use-toast';
import {
  ImagesSection,
  useProducerEditorState,
} from '@/features/producer-editor';
import { ProducerSectionHeader } from '@/components/producer/ProducerSectionHeader';
import { useProducerFormData } from '@/hooks/useProducerFormData';
import { useProducerAccess } from '@/hooks/useProducerAccess';
import { useDraftSessionId } from '@/hooks/useDraftSessionId';

export default function ProducerBilder() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading, error, refetch } = useProducerById(farmId);
  const updateProducerMutation = useUpdateProducer();
  const { hasCapability } = useProducerAccess(farmId !== 'demo' ? farmId : undefined);
  const draftSessionId = useDraftSessionId(`producer-media-${farmId ?? 'demo'}`);

  const isDemo = farmId === 'demo';
  const canEdit = isDemo || hasCapability('canManageMedia');

  const { initialFormData } = useProducerFormData(
    producer,
    isDemo,
    farmId
  );

  const editorState = useProducerEditorState({
    initialData: initialFormData,
    onSave: async (_data) => {
      if (isDemo) {
        toast({ title: 'Sparat', description: 'Sparat (demo).' });
        return;
      }
      if (!producer) return;
      // Media is now persisted via media_assets – no legacy fields to save
      toast({ title: 'Sparat', description: 'Dina ändringar har sparats.' });
    },
  });

  if (isLoading && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Laddar..." title="Bilder">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </ProducerLayout>
    );
  }

  if (error && !isDemo) {
    return (
      <ProducerLayout farmId={farmId || 'demo'} producerName="Fel" title="Bilder">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Kunde inte ladda gården.</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => refetch()} className="mt-4">
          Försök igen
        </Button>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout
      farmId={farmId || 'demo'}
      producerName={editorState.formData.name || 'Din gård'}
      title="Bilder"
    >
      <ProducerSectionHeader
        title="Bilder"
        description="Ladda upp omslagsbild och galleri. Bilder sparas automatiskt."
        isReadOnly={!canEdit}
      />

      <ImagesSection
        producerId={isDemo ? undefined : producer?.id}
        draftSessionId={draftSessionId}
      />
    </ProducerLayout>
  );
}
